import React, { useState, useEffect, lazy, Suspense } from "react";
import "./Navbar.css";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { getCustomerToken, clearCustomerToken, customerApi } from "../../api/client";
import { RiMenu2Line, RiShoppingBagLine } from "react-icons/ri";
import { FiSearch, FiHeart, FiSliders } from "react-icons/fi";
import { FaRegUser } from "react-icons/fa6";
import { MdOutlineClose } from "react-icons/md";
import { FaFacebookF, FaInstagram, FaYoutube, FaPinterest } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import Badge from "@mui/material/Badge";
import { getMenu } from "../../api/storefront";
import { sanitizeStorefrontUrl } from "../../utils/storefrontUrl";

const SearchCompanion = lazy(() => import("./SearchCompanion/SearchCompanion"));
const logo = `${process.env.PUBLIC_URL || ""}/gemlogo.png`;

/** Default storefront header links. Festive Vibes and Occasions have their own dedicated landing pages. */
const FALLBACK_LINKS = [
  { label: "Collections", url: "/shop" },
  { label: "Mens", url: "/shop?gender=male" },
  { label: "Womens", url: "/shop?gender=female" },
  { label: "Festive Vibes", url: "/festive-vibes" },
  { label: "Occasions", url: "/occasions" },
];

/** When false (default), always use FALLBACK_LINKS. Set REACT_APP_HEADER_MENU_FROM_API=true to use Admin → Menu (API /api/menu?slug=header). */
const HEADER_MENU_FROM_API = process.env.REACT_APP_HEADER_MENU_FROM_API === "true";

function fallbackNavAsTree() {
  return FALLBACK_LINKS.map((l) => ({
    id: l.label,
    label: l.label,
    url: l.url,
    children: [],
    is_mega: false,
    mega_config: null,
  }));
}

function isExternal(url) {
  return typeof url === "string" && (url.startsWith("http://") || url.startsWith("https://"));
}

function NavLink({ to, children, onClick, className = "" }) {
  const safeTo = typeof to === "string" ? sanitizeStorefrontUrl(to) : to;
  if (isExternal(safeTo)) {
    return (
      <a href={safeTo} target="_blank" rel="noopener noreferrer" className={className} onClick={onClick}>
        {children}
      </a>
    );
  }
  return (
    <Link to={safeTo || "#"} onClick={onClick} className={className}>
      {children}
    </Link>
  );
}

const Navbar = () => {
  const navigate = useNavigate();
  const cart = useSelector((state) => state.cart);
  const wishlistCount = useSelector((state) => state.wishlist.items.length);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [companionOpen, setCompanionOpen] = useState(false);
  const [desktopSearchOpen, setDesktopSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [menuItems, setMenuItems] = useState([]);
  const [desktopOpenId, setDesktopOpenId] = useState(null);
  const customerToken = getCustomerToken();

  const openCompanion = () => {
    setCompanionOpen(true);
    setMobileMenuOpen(false);
  };

  const submitProductSearch = (e) => {
    e.preventDefault();
    const q = searchText.trim();
    if (!q) return;
    setMobileMenuOpen(false);
    navigate(`/shop?q=${encodeURIComponent(q)}`);
    setDesktopSearchOpen(false);
  };

  const handleCustomerLogout = async () => {
    try {
      if (getCustomerToken()) {
        await customerApi("/api/customer/cart-abandonment/cancel", { method: "POST" });
      }
    } catch {
      /* ignore */
    }
    clearCustomerToken();
    setMobileMenuOpen(false);
    navigate("/", { replace: true });
  };

  useEffect(() => {
    if (!HEADER_MENU_FROM_API) return;
    getMenu("header").then((tree) => {
      if (Array.isArray(tree) && tree.length > 0) setMenuItems(tree);
    });
  }, []);

  useEffect(() => {
    if (!desktopOpenId) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setDesktopOpenId(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [desktopOpenId]);

  const toggleMobileMenu = () => {
    const willOpen = !mobileMenuOpen;
    setMobileMenuOpen(willOpen);
    document.body.style.overflow = willOpen ? "hidden" : "";
  };

  // Release scroll lock if component unmounts while mobile menu is open
  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeMenus = () => {
    setDesktopOpenId(null);
    setMobileMenuOpen(false);
  };

  const navLinks =
    HEADER_MENU_FROM_API && menuItems.length > 0 ? menuItems : fallbackNavAsTree();

  return (
    <>
      {/* Desktop Menu */}
      <nav className="navBar">
        <div className="logoLinkContainer">
          <div className="logoContainer">
            <Link to="/" onClick={scrollToTop} className="logoWithAccent">
              <img src={logo} alt="Gem Fashion Studios" />
            </Link>
          </div>
          <div className="linkContainer">
            <ul>
              {navLinks.map((item) => {
                const hasMega = item.is_mega && item.mega_config?.columns?.length > 0;
                const hasChildren = item.children?.length > 0;

                if (hasMega) {
                  return (
                    <li
                      key={item.id}
                      className="navItemWithDropdown"
                      onMouseEnter={() => setDesktopOpenId(item.id)}
                      onMouseLeave={() => setDesktopOpenId(null)}
                    >
                      <span
                        className="navItemTrigger"
                        role="button"
                        tabIndex={0}
                        aria-haspopup="true"
                        aria-expanded={desktopOpenId === item.id}
                        aria-label={`${item.label} menu`}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDesktopOpenId(desktopOpenId === item.id ? null : item.id); } }}
                      >
                        {item.label}
                      </span>
                      <div className="navDropdown megaDropdown" role="menu" aria-label={item.label} id={`mega-${item.id}`}>
                        <div className="megaDropdownInner">
                          {(item.mega_config.columns || []).map((col, idx) => (
                            <div key={idx} className="megaColumn">
                              {col.title && <div className="megaColumnTitle">{col.title}</div>}
                              {col.type === "image" ? (
                                <NavLink to={col.link} onClick={closeMenus} className="megaImageLink">
                                  {col.imageUrl && <img src={col.imageUrl} alt="" />}
                                  {col.title && !col.imageUrl && col.title}
                                </NavLink>
                              ) : (
                                <ul>
                                  {(col.links || []).map((link, i) => (
                                    <li key={i}>
                                      <NavLink to={link.url} onClick={closeMenus}>{link.label}</NavLink>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </li>
                  );
                }
                if (hasChildren) {
                  return (
                    <li
                      key={item.id}
                      className="navItemWithDropdown"
                      onMouseEnter={() => setDesktopOpenId(item.id)}
                      onMouseLeave={() => setDesktopOpenId(null)}
                    >
                      <span
                        className="navItemTrigger"
                        role="button"
                        tabIndex={0}
                        aria-haspopup="true"
                        aria-expanded={desktopOpenId === item.id}
                        aria-label={`${item.label} menu`}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDesktopOpenId(desktopOpenId === item.id ? null : item.id); } }}
                      >
                        {item.label}
                      </span>
                      <div className="navDropdown simpleDropdown" role="menu" aria-label={item.label} id={`dropdown-${item.id}`}>
                        <ul>
                          {item.children.map((ch) => (
                            <li key={ch.id}>
                              <NavLink to={ch.url} onClick={closeMenus}>{ch.label}</NavLink>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </li>
                  );
                }
                return (
                  <li key={item.id}>
                    <NavLink to={item.url} onClick={scrollToTop}>
                      {item.label}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
        <div className="iconContainer">
          <form
            className={`navSearchForm ${desktopSearchOpen ? "isOpen" : ""}`}
            onSubmit={submitProductSearch}
            role="search"
            aria-label="Search products"
          >
            {desktopSearchOpen && (
              <input
                type="search"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setDesktopSearchOpen(false);
                    e.currentTarget.blur();
                  }
                }}
                placeholder="Search products"
                aria-label="Search products"
                autoFocus
              />
            )}
            <button
              type={desktopSearchOpen ? "submit" : "button"}
              className="navIconButton"
              onClick={() => {
                if (!desktopSearchOpen) setDesktopSearchOpen(true);
              }}
              aria-label={desktopSearchOpen ? "Submit product search" : "Open product search"}
              title={desktopSearchOpen ? "Search" : "Search products"}
            >
              <FiSearch size={20} aria-hidden="true" />
            </button>
          </form>
          <button
            type="button"
            className="navIconButton navAdvancedSearchBtn"
            onClick={openCompanion}
            aria-label="Open advanced product discovery"
            title="Advanced discovery"
          >
            <FiSliders size={20} aria-hidden="true" />
          </button>
          {customerToken ? (
            <>
              <Link to="/account" onClick={scrollToTop} title="My Account">
                <FaRegUser size={22} />
              </Link>
              <button type="button" className="navLogoutBtn" onClick={handleCustomerLogout} title="Logout">
                Logout
              </button>
            </>
          ) : (
            <Link to="/loginSignUp" onClick={scrollToTop} title="Login">
              <FaRegUser size={22} />
            </Link>
          )}
          <Link to="/cart" onClick={scrollToTop}>
            <Badge
              badgeContent={cart.items.length === 0 ? "0" : cart.items.length}
              color="primary"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
              <RiShoppingBagLine size={22} />
            </Badge>
          </Link>
          <Link to="/wishlist" onClick={scrollToTop} title="Wishlist" aria-label="Open wishlist">
            <Badge
              badgeContent={wishlistCount}
              color="primary"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
              <FiHeart size={22} />
            </Badge>
          </Link>
        </div>
      </nav>

      {companionOpen && (
        <Suspense fallback={null}>
          <SearchCompanion onClose={() => setCompanionOpen(false)} />
        </Suspense>
      )}

      {/* Mobile Menu */}
      <nav>
        <div className="mobile-nav">
          {mobileMenuOpen ? (
            <MdOutlineClose size={22} onClick={toggleMobileMenu} />
          ) : (
            <RiMenu2Line size={22} onClick={toggleMobileMenu} />
          )}
          <div className="logoContainer">
            <Link to="/" className="logoWithAccent">
              <img src={logo} alt="Gem Fashion Studios" />
            </Link>
          </div>
          <Link to="/cart" onClick={scrollToTop} className="mobileCartLink" aria-label="Open shopping cart">
            <Badge
              badgeContent={cart.items.length === 0 ? "0" : cart.items.length}
              color="primary"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
              <RiShoppingBagLine size={22} color="black" />
            </Badge>
          </Link>
        </div>
        <div className={`mobile-menu ${mobileMenuOpen ? "open" : ""}`}>
          <div className="mobile-menuTop">
            <div className="mobile-menuSearchBar">
              <div className="mobile-menuSearchBarContainer">
                <form className="mobileSearchForm" onSubmit={submitProductSearch} role="search" aria-label="Search products">
                  <input type="search" placeholder="Search products" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
                  <button type="submit" className="mobile-menuSearchIconBtn" aria-label="Search products">
                    <FiSearch size={22} />
                  </button>
                </form>
                <button type="button" className="mobileAdvancedSearchBtn" onClick={openCompanion} aria-label="Open advanced discovery">
                  Advanced discovery
                </button>
              </div>
            </div>
            <div className="mobile-menuList">
              <ul>
                {navLinks.map((item) => {
                  const hasMega = item.is_mega && item.mega_config?.columns?.length > 0;
                  const hasChildren = item.children?.length > 0;

                  if (hasMega) {
                    return (
                      <li key={item.id} className="mobileNavItemWithSub">
                        <span className="mobileNavLabel">{item.label}</span>
                        <ul className="mobileNavSubList">
                          {(item.mega_config.columns || []).map((col, idx) => (
                            <li key={idx}>
                              {col.title && <div className="mobileNavSubTitle">{col.title}</div>}
                              {col.type === "image" ? (
                                <NavLink to={col.link} onClick={toggleMobileMenu}>{col.title || "Link"}</NavLink>
                              ) : (
                                (col.links || []).map((link, i) => (
                                  <NavLink key={i} to={link.url} onClick={toggleMobileMenu} className="mobileNavSubLink">
                                    {link.label}
                                  </NavLink>
                                ))
                              )}
                            </li>
                          ))}
                        </ul>
                      </li>
                    );
                  }
                  if (hasChildren) {
                    return (
                      <li key={item.id} className="mobileNavItemWithSub">
                        <span className="mobileNavLabel">{item.label}</span>
                        <ul className="mobileNavSubList">
                          {item.children.map((ch) => (
                            <li key={ch.id}>
                              <NavLink to={ch.url} onClick={toggleMobileMenu}>{ch.label}</NavLink>
                            </li>
                          ))}
                        </ul>
                      </li>
                    );
                  }
                  return (
                    <li key={item.id}>
                      <NavLink to={item.url} onClick={toggleMobileMenu}>
                        {item.label}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          <div className="mobile-menuFooter">
            <div className="mobile-menuFooterLogin">
              {customerToken ? (
                <>
                  <Link to="/account" onClick={toggleMobileMenu}>
                    <FaRegUser />
                    <p>My Account</p>
                  </Link>
                  <button type="button" className="mobileLogoutBtn" onClick={() => { handleCustomerLogout(); }}>
                    Logout
                  </button>
                </>
              ) : (
                <Link to="/loginSignUp" onClick={toggleMobileMenu}>
                  <FaRegUser />
                  <p>Login</p>
                </Link>
              )}
            </div>
            <div className="mobile-menuFooterLangCurrency">
              <div className="mobile-menuFooterLang">
                <p>Language</p>
                <select name="language" id="language">
                  <option value="english">United States | English</option>
                  <option value="Hindi">Hindi</option>
                  <option value="Germany">Germany</option>
                  <option value="French">French</option>
                </select>
              </div>
              <div className="mobile-menuFooterCurrency">
                <p>Currency</p>
                <select name="currency" id="currency">
                  <option value="INR">₹ INR</option>
                  <option value="USD">$ USD</option>
                  <option value="EUR">€ EUR</option>
                  <option value="GBP">£ GBP</option>
                </select>
              </div>
            </div>
            <div className="mobile-menuSocial_links">
              <FaFacebookF />
              <FaXTwitter />
              <FaInstagram />
              <FaYoutube />
              <FaPinterest />
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
